import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import {ReduxState} from '../../../store/types';
import {Voicemail} from '../../../store/actions/payloads';
import {initialState, RecordingsPlayingState, RecordingsPlayingStatus,} from '../../../store/reducers/calls/reducer';
import {
    generateAccountColumns,
    generateColumns,
    initialVoicemailDetailsDialogProps,
    saveObjectUrl,
    useStyles,
} from '../utils/VoicemailsDialog.utils';
import {VoicemailDetailsDialogProps} from '../utils/VoicemailDetailsDialog.utils';
import {actions} from '../../../store';
import {useVoicemailsFilter} from './useVoicemailsFilter';
import {useVoicemailsData} from './useVoicemailsData';
import {useVoicemailsPagination} from './useVoicemailsPagination';
import {useVoicemailFileDownload} from './useVoicemailFileDownload';
import {useVoicemailDeleteDialog} from './useVoicemailDeleteDialog';
import {Column} from "react-table";
import {playbackIdentityOf, voicemailPlaybackKey} from '../../../utils/playbackIdentity';

export interface UseVoicemailsParams {
    i_account?: number;
    extensionId?: string;
    extensionName?: string;
    onDataRefresh?: () => void;
    faxMode?: boolean;
}

const DEFAULT_PAGE_SIZE = 10;

export const useVoicemails = ({
    i_account,
    extensionId = '',
    extensionName = '',
    onDataRefresh,
    faxMode = false,
}: UseVoicemailsParams) => {
    const classes = useStyles({forAccount: i_account === undefined});
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Local state
    const [detailsDialogProps, setDetailsDialogProps] = useState<VoicemailDetailsDialogProps>(
        initialVoicemailDetailsDialogProps,
    );
    const closeResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Delete dialog hook
    const {
        dialogVisible,
        messageToDelete,
        onDeleteMessage,
        closeDialog,
    } = useVoicemailDeleteDialog();

    // Redux selectors
    const timezoneOffset = useSelector<ReduxState, number>(
        (state) => state.generic.sessionData?.tz_offset || 0,
    );
    const userDateTimeFormat = useSelector<ReduxState, string>(
        (state) =>
            state.accountControlPanel?.accountDetails?.out_date_time_format
            || state.generic.globalCustomerInfo?.customer_info?.out_date_time_format
            || state.generic.sessionData?.out_date_time_format
            || '',
    );
    const {
        accountVoicemails,
        isUpdatingMessageFlag,
        isDeletingVoicemail,
    } = useSelector((state: ReduxState) => state.voicemails);
    const recordingsPlayer: RecordingsPlayingState = useSelector<
        ReduxState,
        RecordingsPlayingState
    >((state) => state.calls.recordingsPlayer);

    // Derived state
    const isLoadingVoicemails = useMemo(() => {
        return accountVoicemails?.some((e) => e.i_account === i_account && e.isLoadingVoicemails) ?? false;
    }, [accountVoicemails, i_account]);

    // Voicemails data hook
    const { voicemails, filteredCount, getVoicemails,
        forceFirstPage,
        setForceFirstPage } = useVoicemailsData({
        // @ts-ignore
        accountVoicemails,
        i_account: i_account ?? 0,
        timezoneOffset,
        userDateTimeFormat,
        defaultPageSize: DEFAULT_PAGE_SIZE,
    });

    // File download hook
    const downloadByMessageUid = useVoicemailFileDownload({ i_account });

    // Fetch voicemails
    const fetchData = useCallback(() => {
        dispatch(actions.getVoicemailList.request({ i_account }));
    }, [dispatch, i_account]);

    // Filter hook
    const {
        values,
        errors,
        handleSubmit,
        setFieldError,
        resetForm,
        onFromChange,
        onTillChange,
        onSearchClick,
    } = useVoicemailsFilter({
        timezoneOffset,
        userDateTimeFormat,
        onSubmit: (form) => {
            fetchData();
            getVoicemails(form);
        },
    });

    // Pagination hook
    const { onPageChange, onPageSizeChange } = useVoicemailsPagination(
        setForceFirstPage
    );

    // Delete handler
    const onClickDelete = useCallback(() => {
        if (messageToDelete == null) return;
        dispatch(
            actions.deleteVoicemail.request({
                message_uids: [messageToDelete],
                i_account,
                callback: () => {
                    closeDialog();
                    onDataRefresh?.() ?? fetchData();
                },
            }),
        );
    }, [dispatch, i_account, messageToDelete, closeDialog, onDataRefresh, fetchData]);

    // Starred handler
    const onClickStarred = useCallback(
        (message_id: number, newValue: boolean) => {
            dispatch(
                actions.setMailboxMessagesFlag.request({
                    action: newValue ? 'set_flag' : 'remove_flag',
                    flag: 'Flagged',
                    i_account,
                    message_uids: [message_id],
                }),
            );
        },
        [dispatch, i_account],
    );

    // Details dialog handlers
    const onCloseDetailsDialog = useCallback(() => {
        dispatch(actions.internalUpdateRecordingsPlayingState({ ...initialState.internalRecordingsPlayer }));
        setDetailsDialogProps((prev) => ({ ...prev, isOpen: false }));
        if (closeResetTimeoutRef.current) {
            clearTimeout(closeResetTimeoutRef.current);
        }
        closeResetTimeoutRef.current = setTimeout(() => {
            setDetailsDialogProps(initialVoicemailDetailsDialogProps);
            closeResetTimeoutRef.current = null;
        }, 500);
    }, [dispatch]);

    useEffect(() => {
        return () => {
            if (closeResetTimeoutRef.current) {
                clearTimeout(closeResetTimeoutRef.current);
                closeResetTimeoutRef.current = null;
            }
        };
    }, []);

    const onClickDetails = useCallback(
        (voicemail: Voicemail) => {
            if (closeResetTimeoutRef.current) {
                clearTimeout(closeResetTimeoutRef.current);
                closeResetTimeoutRef.current = null;
            }
            setDetailsDialogProps({
                isOpen: true,
                voicemail,
                timezoneOffset,
                onDeleteClick: () => onDeleteMessage(voicemail.message_uid),
                onDownloadClick: () => {
                    downloadByMessageUid(
                        voicemail.message_uid,
                        (url) => saveObjectUrl(url, `voicemail_${voicemail.message_uid}.wav`),
                        undefined,
                    );
                },
                extensionId,
                extensionName,
                toggleVisibility: onCloseDetailsDialog,
                i_account,
                onClickStarred,
                downloadByMessageUid,
                isFax: false,
            });
        },
        [
            timezoneOffset,
            extensionId,
            extensionName,
            i_account,
            onDeleteMessage,
            downloadByMessageUid,
            onClickStarred,
            onCloseDetailsDialog,
        ],
    );

    // A single useMemo with a constant dependency array: calling useMemo
    // conditionally (per branch) breaks the rules of hooks and triggers the
    // "useMemo dependencies changed size between renders" warning when i_account toggles.
    const columns: Column<Voicemail>[] = useMemo(
        () =>
            !i_account
                ? generateAccountColumns(
                    t,
                    classes,
                    timezoneOffset,
                    userDateTimeFormat,
                    downloadByMessageUid,
                    onDeleteMessage,
                    onClickStarred,
                    onClickDetails,
                )
                : generateColumns(
                    t,
                    classes,
                    timezoneOffset,
                    userDateTimeFormat,
                    downloadByMessageUid,
                    onDeleteMessage,
                    onClickStarred,
                    onClickDetails,
                    faxMode,
                    false,
                ),
        [
            i_account,
            t,
            classes,
            timezoneOffset,
            userDateTimeFormat,
            downloadByMessageUid,
            onDeleteMessage,
            onClickStarred,
            onClickDetails,
            faxMode,
        ],
    );



    // Transform data for grid
    const data = useMemo(
        () => voicemails?.map((v) => ({ id: v.message_uid, ...v })),
        [voicemails],
    );

    // Row style for playing voicemail
    // @ts-ignore
    const getRowStyle = useCallback(
        (row: { original: { message_uid?: number } }) => {
            const messageId = row.original.message_uid;
            if (
                messageId &&
                (recordingsPlayer.status === RecordingsPlayingStatus.playing ||
                    recordingsPlayer.status === RecordingsPlayingStatus.paused)
            ) {
                if (playbackIdentityOf(recordingsPlayer?.currentFile) === voicemailPlaybackKey(messageId)) {
                    // @ts-ignore
                    return classes.playingMusicRow;
                }
            }
            return '';
        },
        // @ts-ignore
        [recordingsPlayer, classes.playingMusicRow],
    );

    // Sync voicemails when data changes from backend (e.g. flag updates)
    // Pass resetPage=false to preserve the current page position
    useEffect(() => {
        getVoicemails(values, DEFAULT_PAGE_SIZE, 0, false);
    }, [accountVoicemails, getVoicemails]);

    return {
        // Styles & i18n
        classes,
        t,
        userDateTimeFormat,

        // State
        isLoadingVoicemails,
        isUpdatingMessageFlag,
        isDeletingVoicemail,
        detailsDialogProps,
        dialogVisible,

        // Data
        data,
        columns,
        filteredCount,

        // Filter props
        filterProps: {
            values,
            errors,
            handleSubmit,
            setFieldError,
            onFromChange,
            onTillChange,
            onSearchClick,
        },

        // Pagination
        onPageChange,
        onPageSizeChange,
        initialPageSize: DEFAULT_PAGE_SIZE,

        // Actions
        fetchData,
        resetForm,
        onClickDelete,
        onCancelDeleteClick: closeDialog,
        getRowStyle,
        forceFirstPage,
    };
};

export type UseVoicemailsReturn = ReturnType<typeof useVoicemails>;